import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { DTSelectionService } from 'src/app/digital-twin/selection/selection.service';
import { Graphics } from 'src/app/digital-twin/graphics/graphics';
import { ELEMENTSTYPESIDS } from '@phdsoft/common';

export interface SelectionRegion {
  regionId: string;
  name: string;
  selected: boolean;
  visible: boolean;
  sections: SelectionSection[];
}

export interface SelectionSection {
  sectionId: string;
  regionId: string;
  name: string;
  selected: boolean;
  visible: boolean;
  elements: SelectionElement[];
  includeOther: boolean;
}

export interface SelectionElement {
  elementId: string;
  sectionId: string;
  regionId: string;
  name: string;
  selected: boolean;
}

@Component({
  selector: 'phdsoft-region-selection',
  templateUrl: './region-selection.component.html',
  styleUrls: ['./region-selection.component.scss']
})
export class RegionSelectionComponent implements OnInit, OnChanges {

  @Input() inputRegions: any[] = [];
  @Output() selectedElementIds = new EventEmitter<string[]>();
  @Output() selectedRegionsData = new EventEmitter<any[]>();

  regions: SelectionRegion[] = [];

  private readonly availableElementTypes = [
    ELEMENTSTYPESIDS.PLATE,
    // ELEMENTSTYPESIDS.BRACKET,
    // ELEMENTSTYPESIDS.FACEPLATE,
    // ELEMENTSTYPESIDS.STIFFENER
  ];

  constructor(
    private assetManager: AssetManager,
    private selectionService: DTSelectionService,
    private graphics: Graphics
  ) { }

  ngOnInit(): void {
    this.buildRegions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.inputRegions && !changes.inputRegions.firstChange) {
      this.buildRegions();
    }
  }

  buildRegions(): void {
    if (!this.inputRegions || this.inputRegions.length === 0) return;

    const existingRegionsMap = new Map<string, SelectionRegion>();
    this.regions.forEach(r => existingRegionsMap.set(r.regionId, r));

    // Build map of input regions for selection state updates
    const inputRegionsMap = new Map<string, any>();
    this.inputRegions.forEach(r => inputRegionsMap.set(r.id || r.RegionId, r));

    // If we already have regions, only update selection state (preserve full list for display)
    if (this.regions.length > 0) {
      this.regions.forEach(region => {
        const inputRegion = inputRegionsMap.get(region.regionId);
        if (inputRegion) {
          region.selected = inputRegion.selected || inputRegion.RegionChecked || false;

          const inputSectionsMap = new Map<string, any>();
          (inputRegion.children || inputRegion.Sections || []).forEach(s =>
            inputSectionsMap.set(s.id || s.SectionId, s)
          );

          region.sections.forEach(section => {
            const inputSection = inputSectionsMap.get(section.sectionId);
            if (inputSection) {
              section.selected = inputSection.selected || inputSection.SectionChecked || false;
            }
          });
        }
      });
      return;
    }

    // Initial build from inputRegions
    this.regions = this.inputRegions.map(region => {
      const regionId = region.id || region.RegionId;
      const existingRegion = existingRegionsMap.get(regionId);
      const existingSectionsMap = new Map<string, SelectionSection>();
      existingRegion?.sections.forEach(s => existingSectionsMap.set(s.sectionId, s));

      return {
        regionId,
        name: region.name || region.RegionName,
        selected: region.selected || region.RegionChecked || false,
        visible: existingRegion?.visible ?? false,
        sections: (region.children || region.Sections || []).map(section => {
          const sectionId = section.id || section.SectionId;
          const existingSection = existingSectionsMap.get(sectionId);
          return {
            sectionId,
            regionId,
            name: section.name || section.SectionName,
            selected: section.selected || section.SectionChecked || false,
            visible: existingSection?.visible ?? false,
            elements: existingSection?.elements ?? [],
            includeOther: existingSection?.includeOther ?? false
          };
        }).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
      };
    });
  }

  toggleRegion(region: SelectionRegion): void {
    const opening = !region.visible;
    this.regions.forEach(r => r.visible = false);
    region.visible = opening;
  }

  toggleSection(section: SelectionSection): void {
    const opening = !section.visible;
    const parentRegion = this.regions.find(r => r.regionId === section.regionId);
    parentRegion?.sections.forEach(s => s.visible = false);
    section.visible = opening;
    if (section.visible && section.elements.length === 0) {
      this.loadSectionElements(section);
    }
  }

  loadSectionElements(section: SelectionSection): void {
    const sectionElements = this.assetManager.asset.StructureCampaigns[0]?.Elements?.filter(
      x =>
        x.SectionId === section.sectionId &&
        x.regionId === section.regionId &&
        this.availableElementTypes.includes(x.TypeId.toString())
    ) || [];
    sectionElements.push(...this.assetManager.asset.RepairCampaigns[0]?.Elements?.filter(
      x =>
        x.SectionId === section.sectionId &&
        x.regionId === section.regionId &&
        this.availableElementTypes.includes(x.TypeId.toString())
    ) ?? [])

    section.elements = sectionElements.map(el => ({
      elementId: el.Guid,
      sectionId: section.sectionId,
      regionId: section.regionId,
      name: el.properties?.General?.Label || el.Guid,
      selected: section.selected
    })).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }

  onRegionCheckChange(region: SelectionRegion): void {
    region.selected = !region.selected;
    region.sections.forEach(section => {
      section.selected = region.selected;
      if (section.elements.length === 0) {
        this.loadSectionElements(section);
      }
      section.elements.forEach(el => el.selected = region.selected);
      section.includeOther = region.selected;
    });
    this.emitSelections();
  }

  onSectionCheckChange(region: SelectionRegion, section: SelectionSection): void {
    section.selected = !section.selected;
    if (section.elements.length === 0) {
      this.loadSectionElements(section);
    }
    section.elements.forEach(el => el.selected = section.selected);
    section.includeOther = section.selected;
    this.updateRegionSelection(region);
    this.emitSelections();
  }

  onElementCheckChange(region: SelectionRegion, section: SelectionSection, element: SelectionElement): void {
    element.selected = !element.selected;
    this.updateSectionSelection(section);
    this.updateRegionSelection(region);
    this.emitSelections();
  }

  onOtherCheckChange(region: SelectionRegion, section: SelectionSection): void {
    section.includeOther = !section.includeOther;
    this.updateSectionSelection(section);
    this.updateRegionSelection(region);
    this.emitSelections();
  }

  updateSectionSelection(section: SelectionSection): void {
    const allElementsSelected = section.elements.length === 0 || section.elements.every(el => el.selected);
    section.selected = allElementsSelected && section.includeOther;
  }

  updateRegionSelection(region: SelectionRegion): void {
    region.selected = region.sections.every(s => s.selected);
  }

  isSectionIndeterminate(section: SelectionSection): boolean {
    const selectedCount = section.elements.filter(el => el.selected).length;
    const allElementsSelected = section.elements.length === 0 || selectedCount === section.elements.length;
    const hasAnySelection = selectedCount > 0 || section.includeOther;
    const allSelected = allElementsSelected && section.includeOther;
    return hasAnySelection && !allSelected;
  }

  isRegionIndeterminate(region: SelectionRegion): boolean {
    const hasSelected = region.sections.some(s => s.selected || this.isSectionIndeterminate(s));
    const allSelected = region.sections.every(s => s.selected && !this.isSectionIndeterminate(s));
    return hasSelected && !allSelected;
  }

  getAutoIncludedElementIds(section: SelectionSection): string[] {
    const sectionElements = this.assetManager.asset.StructureCampaigns[0]?.Elements?.filter(
      x =>
        x.SectionId === section.sectionId &&
        x.regionId === section.regionId &&
        !this.availableElementTypes.includes(x.TypeId.toString())
    ) || [];

    return sectionElements.map(el => el.Guid);
  }

  emitSelections(): void {
    const elementIds: string[] = [];

    this.regions.forEach(region => {
      region.sections.forEach(section => {
        if (section.elements.length === 0 && (section.selected || this.isSectionIndeterminate(section))) {
          this.loadSectionElements(section);
        }
        // Add user-selected elements (from filtered list shown in UI)
        section.elements.filter(el => el.selected).forEach(el => {
          elementIds.push(el.elementId);
        });

        // Include "Other" elements if the checkbox is checked
        if (section.includeOther) {
          const otherElementIds = this.getAutoIncludedElementIds(section);
          elementIds.push(...otherElementIds);
        }
      });
    });

    this.selectedElementIds.emit(elementIds);

    // Emit only regions/sections that are selected or partially selected (indeterminate)
    const regionsData = this.regions
      .map(region => {
        const selectedSections = region.sections.filter(
          s => s.selected || this.isSectionIndeterminate(s)
        );
        return {
          id: region.regionId,
          name: region.name,
          selected: region.selected,
          parentId: '',
          item: '',
          elementIds: selectedSections.flatMap(s => [
            ...s.elements.filter(el => el.selected).map(el => el.elementId),
            ...(s.includeOther ? this.getAutoIncludedElementIds(s) : [])
          ]),
          children: selectedSections.map(section => ({
            id: section.sectionId,
            name: section.name,
            selected: section.selected,
            parentId: region.regionId
          }))
        };
      })
      .filter(region => region.children.length > 0);

    this.selectedRegionsData.emit(regionsData);
  }

  getSelectedElementIds(): string[] {
    const elementIds: string[] = [];

    this.regions.forEach(region => {
      region.sections.forEach(section => {
        if (section.elements.length === 0 && (section.selected || this.isSectionIndeterminate(section))) {
          this.loadSectionElements(section);
        }
        // Add user-selected elements (from filtered list shown in UI)
        section.elements.filter(el => el.selected).forEach(el => {
          elementIds.push(el.elementId);
        });

        // Include "Other" elements if the checkbox is checked
        if (section.includeOther) {
          const otherElementIds = this.getAutoIncludedElementIds(section);
          elementIds.push(...otherElementIds);
        }
      });
    });

    return elementIds;
  }

  highlightRegion(region: SelectionRegion): void {
    const assetRegion = this.assetManager.asset.GetRegionsAllTypes()?.find(x => x.Guid === region.regionId);
    if (!assetRegion) return;

    const surfaces: any[] = [];
    assetRegion.sections?.forEach(section => {
      section.surfaceElements?.forEach(element => {
        if (element.SurfaceDesign) {
          surfaces.push(element.SurfaceDesign);
        }
      });
    });

    this.graphics.outlineElement(surfaces);
  }

  highlightSection(section: SelectionSection): void {
    const assetRegion = this.assetManager.asset.GetRegionsAllTypes()?.find(x => x.Guid === section.regionId);
    if (!assetRegion) return;

    const assetSection = assetRegion.sections?.find(x => x.Guid === section.sectionId);
    if (!assetSection) return;

    const surfaces: any[] = [];
    assetSection.surfaceElements?.forEach(element => {
      if (element.SurfaceDesign) {
        surfaces.push(element.SurfaceDesign);
      }
    });

    this.graphics.outlineElement(surfaces);
  }

  highlightElement(element: SelectionElement): void {
    const assetRegion = this.assetManager.asset.GetRegionsAllTypes()?.find(x => x.Guid === element.regionId);
    if (!assetRegion) return;

    const assetSection = assetRegion.sections?.find(x => x.Guid === element.sectionId);
    if (!assetSection) return;

    const assetElement = assetSection.surfaceElements?.find(x => x.Guid === element.elementId);
    if (!assetElement) return;

    this.graphics.outlineElement([assetElement.SurfaceDesign]);
  }
}
